import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Stack,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material'
import {
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { authService } from '../../services/authService'

interface BackupCodesDisplayProps {
  open: boolean
  onClose: () => void
  backupCodes: string[]
  showRegenerate?: boolean
  onRegenerate?: (codes: string[]) => void
}

export const BackupCodesDisplay: React.FC<BackupCodesDisplayProps> = ({
  open,
  onClose,
  backupCodes,
  showRegenerate = false,
  onRegenerate,
}) => {
  const { t } = useTranslation('profile')
  const [showRegenerateForm, setShowRegenerateForm] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const handleClose = () => {
    setShowRegenerateForm(false)
    setVerificationCode('')
    setError(null)
    onClose()
  }

  const handleCopyAll = async () => {
    const codesText = backupCodes.join('\n')
    await navigator.clipboard.writeText(codesText)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleDownload = () => {
    const codesText = `ComplianceAuto 2段階認証バックアップコード\n生成日時: ${new Date().toLocaleString('ja-JP')}\n\n以下のコードは1回限り使用できます。安全な場所に保管してください。\n\n${backupCodes.join('\n')}`
    const blob = new Blob([codesText], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'backup-codes.txt'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleRegenerate = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('6桁の認証コードを入力してください')
      return
    }

    try {
      setLoading(true)
      setError(null)
      const response = await authService.regenerateBackupCodes(verificationCode)
      setShowRegenerateForm(false)
      setVerificationCode('')
      if (onRegenerate) {
        onRegenerate(response.backupCodes)
      }
    } catch (err: any) {
      setError(err.response?.data?.error || '再生成に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6)
    setVerificationCode(value)
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('mfa.backupCodes.title', 'バックアップコード')}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {backupCodes.length > 0 ? (
            <>
              <Alert severity="warning" icon={<WarningIcon />}>
                {t(
                  'mfa.backupCodes.warning',
                  'これらのコードは一度だけ表示されます。安全な場所に保管してください。各コードは1回のみ使用できます。'
                )}
              </Alert>

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 1,
                  }}
                >
                  {backupCodes.map((code, index) => (
                    <Typography
                      key={index}
                      variant="body1"
                      sx={{
                        fontFamily: 'monospace',
                        bgcolor: 'white',
                        p: 1,
                        borderRadius: 0.5,
                        textAlign: 'center',
                      }}
                    >
                      {code}
                    </Typography>
                  ))}
                </Box>
              </Paper>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button variant="outlined" startIcon={<CopyIcon />} onClick={handleCopyAll}>
                  {copied ? t('mfa.copied', 'コピー済み') : t('mfa.backupCodes.copy', 'コピー')}
                </Button>
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>
                  {t('mfa.backupCodes.download', 'ダウンロード')}
                </Button>
              </Box>
            </>
          ) : showRegenerateForm ? (
            <>
              {error && <Alert severity="error">{error}</Alert>}

              <Typography variant="body2" color="text.secondary">
                {t(
                  'mfa.backupCodes.regenerateInstructions',
                  '新しいバックアップコードを生成するには、認証アプリのコードを入力してください。'
                )}
              </Typography>

              <TextField
                label={t('mfa.setup.codeLabel', '認証コード')}
                value={verificationCode}
                onChange={handleCodeChange}
                placeholder="000000"
                inputProps={{
                  maxLength: 6,
                  style: {
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    letterSpacing: '0.5rem',
                    fontFamily: 'monospace',
                  },
                }}
                fullWidth
                autoFocus
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setShowRegenerateForm(false)}
                  disabled={loading}
                >
                  {t('mfa.setup.back', '戻る')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleRegenerate}
                  disabled={loading || verificationCode.length !== 6}
                  fullWidth
                >
                  {loading ? <CircularProgress size={24} /> : t('mfa.backupCodes.generate', '生成')}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'mfa.backupCodes.description',
                  'バックアップコードは、認証アプリにアクセスできない場合にアカウントにログインするために使用できます。'
                )}
              </Typography>

              {showRegenerate && (
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={() => setShowRegenerateForm(true)}
                  fullWidth
                >
                  {t('mfa.backupCodes.regenerate', '新しいコードを生成')}
                </Button>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('common.close', '閉じる')}</Button>
      </DialogActions>
    </Dialog>
  )
}
